import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import authService from '../../services/auth.service';

const ServiceProviderLogin: React.FC = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
  });
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.login({
        identifier: formData.identifier,
        password: formData.password,
      });

      // Fetch user with role populated (login response doesn't include role by default)
      const userWithRole = await authService.getMe();
      const roleType = userWithRole?.role?.type || userWithRole?.role?.attributes?.type;
      
      // Accept both 'service-provider' and 'service_provider' (hyphen or underscore)
      if (roleType !== 'service-provider' && roleType !== 'service_provider') {
        authService.logout();
        setError('Access denied. This portal is for service providers only.');
        return;
      }

      // Update stored user with role information
      localStorage.setItem('user', JSON.stringify(userWithRole));

      // Store service provider flag
      localStorage.setItem('isServiceProvider', 'true');
      navigate('/service-provider/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography component="h1" variant="h4" sx={{ fontWeight: 'bold', color: '#F56A2C' }}>
              DARB ERP
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ mt: 1 }}>
              Service Provider Portal
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="identifier"
              label="Username or Email"
              name="identifier"
              autoComplete="username"
              autoFocus
              value={formData.identifier}
              onChange={handleChange}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5, backgroundColor: '#F56A2C', '&:hover': { backgroundColor: '#D95C26' } }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ServiceProviderLogin;

